import React, { useCallback, useRef } from 'react';
import type { WorkOrderTemplate } from '#/graphql/types';
import List from '#/common/components/List';
import { getWorkOrderTemplateList } from '#/graphql/queries';
import { gql } from '@apollo/client';
import { Filter } from '#/common/components/DataGrid';
import columns from '../WOTemplateDataGrid/columns';
import useWOTemplateDelete from '../../hooks/useWOTemplateDelete';
import useToast from '#/common/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { List as DxList } from 'devextreme-react/list';

export interface WOTemplateListProps {
  permanentFilters?: Filter[];
}

const getWOTemplateListQuery = gql(getWorkOrderTemplateList);

const WOTemplateList: React.FC<WOTemplateListProps> = (props) => {
  const { permanentFilters } = props;

  const navigate = useNavigate();
  const list = useRef<DxList | null>(null);
  const { confirm } = useToast();
  const { deleteWOTemplate } = useWOTemplateDelete();

  const deleteItem = useCallback(
    async (items?: WorkOrderTemplate[], clearSelectedItems?: () => void) => {
      if (!items) {
        return undefined;
      }

      let msg = `Deleting ${items.length === 1 ? 'this template' : 'these templates'} is permanent. Are you sure you want to proceed?`;
      const hasPreventive = items.some(
        (item) => item.category === 'preventive' && item.executionInterval?.length,
      );

      if (hasPreventive) {
        msg =
          items.length === 1
            ? 'This Work Order Template is configured for preventive recurrence. Deleting it will stop all future Work Orders from being created. Existing Work Orders will not be affected. This action is permanent.'
            : 'These templates contain ones configured for preventive recurrence. Deleting them will stop all future Work Orders from being created. Existing Work Orders will not be affected. This action is permanent. Are you sure you want to proceed?';
      }

      const sure = await confirm('error', msg);
      if (!sure) {
        return;
      }
      await deleteWOTemplate(items.map((item) => item.id).join(','));
      list.current && list.current.instance.reload();
      clearSelectedItems && clearSelectedItems();
    },
    [deleteWOTemplate],
  );

  const openTemplate = useCallback(
    (template: WorkOrderTemplate) => navigate(`/work-orders/templates/${template.id}`),
    [navigate],
  );

  return (
    <List<WorkOrderTemplate>
      setRef={(ref) => (list.current = ref)}
      titleKey="title"
      onClick={openTemplate}
      getItemsQuery={getWOTemplateListQuery}
      permanentFilters={permanentFilters}
      subtitleKey="displayNumber"
      bodyFields={[
        { key: 'executionInterval', label: 'Execution Interval' },
        { key: 'assetType.name', label: 'Asset Type' },
        { key: 'manufacturer.name', label: 'Manufacturer' },
        { key: 'model.name', label: 'Model' },
      ]}
      columnList={columns}
      isActionDeleteOnly
      floatingButtonOnClick={deleteItem}
    />
  );
};

export default React.memo(WOTemplateList);
